/**
 * ScanStatusModal - Modal for viewing and managing jobs
 *
 * Features:
 * - Bulk selection with Rerun/Cancel actions
 * - Sorted by updated timestamp (newest first) by default
 *
 * Used in Asset, Vulnerability, and Capability drawers.
 */

import { useMemo, useState } from 'react';

import { Modal } from '@/components/Modal';
import { TableSelectionBar } from '@/components/table/TableSelectionBar';
import { useTableRemountOnTransition } from '@/hooks/useTableRemountOnTransition';
import { JobsActions } from '@/sections/jobs/components/actions';
import { JobsTable } from '@/sections/jobs/components/JobsTable';
import { getJobActionOptions } from '@/sections/jobs/config/actionOptions/getJobActionOptions';
import { Job } from '@/types';

import { ScanStatusModalProps } from './types';

/**
 * ScanStatusModal Component
 *
 * Renders a modal with a jobs table, filtering, search, and bulk actions.
 */
export function ScanStatusModal({
  open,
  onClose,
  title,
  jobs,
  isLoading,
  error,
  refetch,
  actionHandlers,
}: ScanStatusModalProps) {
  // Selection state for bulk actions
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  // Sort state - default to newest first
  const [sort, setSort] = useState<{ orderBy: keyof Job; descending: boolean }>(
    {
      orderBy: 'updated',
      descending: true,
    }
  );

  // Force table remount on empty -> data transition
  const isFilteredEmpty = !isLoading && jobs.length === 0;
  const tableKey = useTableRemountOnTransition(isFilteredEmpty, jobs.length);

  // Selected jobs from keys
  const selectedJobs = useMemo(
    () =>
      selectedRows
        .map(key => jobs.find(job => job.key === key))
        .filter((job): job is Job => job !== undefined),
    [selectedRows, jobs]
  );

  // Handle modal close with state reset
  const handleClose = () => {
    setSelectedRows([]);
    setSort({ orderBy: 'updated', descending: true });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      size="7xl"
      hideFooter
      className="mb-4"
    >
      <TableSelectionBar
        embedded={false}
        alwaysVisible={true}
        isVisible={selectedRows.length > 0}
        selectedCount={selectedJobs.length}
        totalCount={jobs.length}
        hasNextPage={false}
        resourceLabel="jobs"
        emptySelectionMessage="Select jobs to perform bulk actions"
        actions={
          <JobsActions
            jobs={selectedJobs}
            selectedRowsCount={selectedRows.length}
            onClearSelection={() => setSelectedRows([])}
            refetch={refetch}
            hideScanStatus={true}
            hideCreateJob={true}
            disabled={selectedJobs.length === 0}
          />
        }
      />
      <JobsTable
        key={tableKey}
        name="scan-status-jobs"
        jobs={jobs}
        status={isLoading ? 'pending' : 'success'}
        error={error}
        responsive={false}
        enableSelection={true}
        selectedRows={selectedRows}
        onSelectionChange={setSelectedRows}
        maxHeight={480}
        minHeight={480}
        sort={{
          orderBy: sort.orderBy,
          descending: sort.descending,
          onSort: setSort,
        }}
        rowActions={job => ({
          menu: {
            items: getJobActionOptions({
              job,
              handlers: {
                handleRerun: actionHandlers.handleRerun,
                handleCancel: actionHandlers.handleCancel,
                handleDownload:
                  actionHandlers.handleDownload ?? (() => undefined),
              },
            }),
          },
        })}
        emptyState={{
          title: 'No Jobs Found',
          description: 'No jobs available.',
        }}
      />
    </Modal>
  );
}
